import React, { useState, useEffect, useRef } from 'react'
import { 
  Plus,
  Download,
  Users,
  CheckCircle,
  Store,
  Wrench,
  Star
} from 'lucide-react'
import DataTable from '../../components/DataTable/DataTable'
import Breadcrumb from '../../components/Common/Breadcrumb'
import ConfirmationModal from '../../components/ConfirmationModal/ConfirmationModal'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import supplierMasterService from '../../services/supplierMasterService'
import '../../components/ConfirmationModal/ConfirmationModal.css'
// Consolidated CSS is imported globally

const SupplierMaster = () => {
  const navigate = useNavigate()
  const [data, setData] = useState([])
  const [loading, setLoading] = useState(true)
  const [stats, setStats] = useState({
    totalSuppliers: 0,
    activeSuppliers: 0,
    inactiveSuppliers: 0,
    vendorType: 0,
    serviceProviderType: 0,
    manufacturerType: 0,
    averageRating: 0
  })
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [deleteItem, setDeleteItem] = useState(null)
  const hasLoadedRef = useRef(false)
  const hasShownEmptyToastRef = useRef(false)

  // Load data from backend
  useEffect(() => {
    if (!hasLoadedRef.current) {
      hasLoadedRef.current = true
      loadData()
    }
  }, [])

  const loadData = async () => {
    try {
      setLoading(true)
      const [suppliersResponse, statsResponse] = await Promise.all([
        supplierMasterService.getAllSuppliers(),
        supplierMasterService.getSupplierStats()
      ])
      
      if (suppliersResponse.success) {
        // Transform _id to id for frontend compatibility
        const transformedData = (suppliersResponse.data || []).map(supplier => ({
          ...supplier,
          id: supplier._id
        }))
        setData(transformedData)
        // Only show toast if data is empty and we haven't shown it before
        if (suppliersResponse.data?.length === 0 && !hasShownEmptyToastRef.current) {
          hasShownEmptyToastRef.current = true
          toast.info('No supplier records found. Create your first supplier!')
        }
      } else {
        toast.error(suppliersResponse.message || 'Failed to load suppliers')
      }
      
      if (statsResponse.success) {
        setStats(statsResponse.data)
      } else {
        toast.error(statsResponse.message || 'Failed to load statistics')
      }
    } catch (error) {
      console.error('Error loading supplier master records:', error)
      if (error.response?.status === 401) {
        toast.error('Authentication required. Please log in again.')
      } else if (error.response?.status === 403) {
        toast.error('You do not have permission to view suppliers')
      } else {
        if (!error._errorHandled) {
          toast.error('Failed to load supplier master records')
        }
      }
    } finally {
      setLoading(false)
    }
  }

  const getSupplierTypeBadgeClass = (type) => {
    const typeMap = {
      'Vendor': 'active',
      'Service Provider': 'warning',
      'Manufacturer': 'pending',
      'Distributor': 'active',
      'Wholesaler': 'pending',
      'Retailer': 'inactive',
      'Other': 'inactive'
    }
    return typeMap[type] || 'inactive'
  }

  const columns = [
    {
      accessorKey: 'supplierCode',
      header: 'Code',
      cell: ({ getValue }) => (
        <div className="service-code-cell" style={{ textAlign: 'left' }}>
          <span className="code-text">{getValue()}</span>
        </div>
      ),
    },
    {
      accessorKey: 'supplierName',
      header: 'Supplier Name',
      cell: ({ getValue }) => (
        <div className="service-description-cell" style={{ textAlign: 'left', whiteSpace: 'normal', wordWrap: 'break-word' }}>
          <span className="description-text">{getValue() || '-'}</span>
        </div>
      ),
    },
    {
      accessorKey: 'supplierType',
      header: 'Type',
      cell: ({ getValue }) => (
        <div className="active-cell" style={{ textAlign: 'center' }}>
          <span className={`status-badge ${getSupplierTypeBadgeClass(getValue())}`}>
            {getValue() || '-'}
          </span>
        </div>
      ),
    },
    {
      accessorKey: 'primaryContact.email',
      header: 'Email',
      cell: ({ row }) => (
        <div style={{ textAlign: 'left' }}>
          {row.original.primaryContact?.email || '-'}
        </div>
      ),
    },
    {
      accessorKey: 'primaryContact.phone',
      header: 'Phone',
      cell: ({ row }) => (
        <div style={{ textAlign: 'left' }}>
          {row.original.primaryContact?.phone || row.original.primaryContact?.mobile || '-'}
        </div>
      ),
    },
    {
      accessorKey: 'rating',
      header: 'Rating',
      cell: ({ getValue }) => (
        <div style={{ textAlign: 'center', fontWeight: '600' }}>
          {getValue() ? `${getValue()}/5` : '-'}
        </div>
      ),
    },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: ({ getValue }) => (
        <div className="active-cell" style={{ textAlign: 'center' }}>
          <span className={`status-badge ${getValue()?.toLowerCase() === 'active' ? 'active' : getValue()?.toLowerCase() === 'suspended' ? 'warning' : getValue()?.toLowerCase() === 'blacklisted' ? 'danger' : 'inactive'}`}>
            {getValue() || '-'}
          </span>
        </div>
      ),
    },
  ]

  const handleAdd = () => {
    navigate('/procurement/supplier-master/form')
  }

  const handleEdit = (supplierData) => {
    navigate('/procurement/supplier-master/edit', { state: { supplierData } })
  }

  const handleView = (supplierData) => {
    navigate('/procurement/supplier-master/view', { state: { supplierData } })
  }

  const handleDelete = (supplierData) => {
    setDeleteItem(supplierData)
    setShowDeleteModal(true)
  }

  const confirmDelete = async () => {
    if (!deleteItem) return

    try {
      toast.info('Deleting supplier...')
      const response = await supplierMasterService.deleteSupplier(deleteItem._id)
      if (response.success) {
        toast.success('Supplier deleted successfully!', { autoClose: 2000 })
        loadData() // Reload data
      } else {
        toast.error(response.message || 'Failed to delete supplier')
      }
    } catch (error) {
      console.error('Error deleting supplier:', error)
      if (error.response?.status === 404) {
        toast.error('Supplier not found')
      } else if (error.response?.status === 403) {
        toast.error('You do not have permission to delete this supplier')
      } else {
        if (!error._errorHandled) {
          toast.error('Failed to delete supplier. Please try again.')
        }
      }
    } finally {
      setShowDeleteModal(false)
      setDeleteItem(null)
    }
  }

  const cancelDelete = () => {
    setShowDeleteModal(false)
    setDeleteItem(null)
  }

  const handleExport = () => {
    toast.info('Export functionality coming soon!')
  }

  const breadcrumbItems = [
    { label: 'Procurement', path: '/procurement' },
    { label: 'Masters', path: '/procurement?tab=masters' },
    { label: 'Suppliers', isCurrent: true }
  ]

  return (
    <div className="sales-dashboard">
      <Breadcrumb items={breadcrumbItems} />

      <div className="page-header">
        <div className="page-title">
          <h1>Suppliers</h1>
          <p>Vendor profiles and supplier information</p>
        </div>
        <div className="page-actions">
          <button type="button" className="btn-primary" onClick={handleAdd}>
            <Plus size={16} />
            New Supplier
          </button>
          <button type="button" className="btn btn-outline-secondary btn-sm" onClick={handleExport}>
            <Download size={16} />
            Export
          </button>
        </div>
      </div>

      <div className="dashboard-metrics-grid">
        <div className="metric-card deals">
          <div className="metric-header">
            <div className="metric-icon">
              <Users size={16} />
            </div>
            <div className="metric-trend positive">
              <span>Total</span>
            </div>
          </div>
          <div className="metric-value">{stats.totalSuppliers || 0}</div>
          <div className="metric-title">Total Suppliers</div>
          <div className="metric-subtitle">All vendor records</div>
        </div>

        <div className="metric-card revenue">
          <div className="metric-header">
            <div className="metric-icon">
              <CheckCircle size={16} />
            </div>
            <div className="metric-trend positive">
              <span>Active</span>
            </div>
          </div>
          <div className="metric-value">{stats.activeSuppliers || 0}</div>
          <div className="metric-title">Active Suppliers</div>
          <div className="metric-subtitle">Currently engaged</div>
        </div>

        <div className="metric-card customers">
          <div className="metric-header">
            <div className="metric-icon">
              <Store size={16} />
            </div>
            <div className="metric-trend neutral">
              <span>Vendors</span>
            </div>
          </div>
          <div className="metric-value">{stats.vendorType || 0}</div>
          <div className="metric-title">Vendors</div>
          <div className="metric-subtitle">Vendor type</div>
        </div>

        <div className="metric-card conversion">
          <div className="metric-header">
            <div className="metric-icon">
              <Wrench size={16} />
            </div>
            <div className="metric-trend neutral">
              <span>Services</span>
            </div>
          </div>
          <div className="metric-value">{stats.serviceProviderType || 0}</div>
          <div className="metric-title">Service Providers</div>
          <div className="metric-subtitle">Service type</div>
        </div>

        <div className="metric-card burn">
          <div className="metric-header">
            <div className="metric-icon">
              <Star size={16} />
            </div>
            <div className="metric-trend positive">
              <span>Rating</span>
            </div>
          </div>
          <div className="metric-value">{stats.averageRating ? stats.averageRating.toFixed(1) : '0.0'}</div>
          <div className="metric-title">Average Rating</div>
          <div className="metric-subtitle">Across suppliers</div>
        </div>
      </div>

      <div className="data-table-section">
        <DataTable
          data={data}
          columns={columns}
          loading={loading}
          onEdit={handleEdit}
          onView={handleView}
          onDelete={handleDelete}
          enableSelection={false}
          noCard={true}
          className="supplier-master-table"
          pageSize={10}
        />
      </div>

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={cancelDelete}
        onConfirm={confirmDelete}
        title="Delete Supplier"
        message={`Are you sure you want to delete supplier "${deleteItem?.supplierName}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        type="danger"
      />
    </div>
  )
}

export default SupplierMaster

